import React, { useState, useMemo, useRef } from 'react';
import AdminSearchTable from '../Admin/AdminSearchTable';
import FilterIcon from '../../SVG/Filter';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import useClickOutside from '../Shared/useClickOutside';


const CommissionDetailSection = ({ model }) => {
    const allClaims = model?.commissions || [];

    const downlines = useMemo(() => {
        const list = model?.downlines || [];
        return [...list].sort((a, b) =>
            a.agentCode?.localeCompare(b.agentCode)
        );
    }, [model?.downlines]);

    const [selectedPaidAgt, setSelectedPaidAgt] = useState(''); // AgtCode or '' for all
    const [selectedWritingAgt, setSelectedWritingAgt] = useState(''); // AgtCode or '' for all

    const entryPickerRef = useRef(null);
    const reportPickerRef = useRef(null);

    useClickOutside(entryPickerRef, () => setShowEntryPicker(false));
    useClickOutside(reportPickerRef, () => setShowReportPicker(false));

    // Entry Date Range state
    const [entryRange, setEntryRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);
    const [entryFilterActive, setEntryFilterActive] = useState(false);
    const [showEntryPicker, setShowEntryPicker] = useState(false);

    // Report Date Range state
    const [reportRange, setReportRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);
    const [reportFilterActive, setReportFilterActive] = useState(false);
    const [showReportPicker, setShowReportPicker] = useState(false);

    const columns = [
        { label: 'Writing Agt', accessor: 'writingAgent' },
        { label: 'Writing Agt Name', accessor: 'writingAgentName' },
        { label: 'Paid Agt', accessor: 'paidAgent' },
        { label: 'Paid Agt Name', accessor: 'paidAgentName' },
        { label: 'Share', accessor: 'share' },
        { label: 'Policy Number', accessor: 'polNo' },
        { label: 'Owner', accessor: 'ownerDisplayName' },
        { label: 'Premium Amount', accessor: 'premiumAmountDisplay', sortType: 'currency' },
        { label: 'Commission Paid', accessor: 'commissionPaidDisplay', sortType: 'currency' },
        { label: 'Entry Date', accessor: 'entryDateDisplay' },
        { label: 'Comm Stmt Date', accessor: 'reportDateDisplay' },
    ];

    const formatDateLabel = (date) => {
        if (!date) return '';
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    const formatRangeLabel = (active, range) => {
        if (!active) return 'All Dates';
        const { startDate, endDate } = range[0] || {};
        if (!startDate || !endDate) return 'All Dates';
        return `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`;
    };

    // Filter claims based on entry date range + report date range + downline
    const filteredClaims = useMemo(() => {
        const entryStart = entryFilterActive ? entryRange[0].startDate : null;
        const entryEnd = entryFilterActive ? entryRange[0].endDate : null;

        const reportStart = reportFilterActive ? reportRange[0].startDate : null;
        const reportEnd = reportFilterActive ? reportRange[0].endDate : null;

        return allClaims.filter(claim => {
            const entryDate = claim.entryDate ? new Date(claim.entryDate) : null;
            const reportDate = claim.reportDate ? new Date(claim.reportDate) : null;

            // --- entry date range filter ---
            if (entryFilterActive && entryDate) {
                if (entryStart && entryDate < entryStart) return false;
                if (entryEnd && entryDate > entryEnd) return false;
            }

            // --- report date range filter ---
            if (reportFilterActive && reportDate) {
                if (reportStart && reportDate < reportStart) return false;
                if (reportEnd && reportDate > reportEnd) return false;
            }

            // --- downline filter ---
            if (selectedWritingAgt) {
                if (claim.writingAgent !== selectedWritingAgt) return false;
            }
            if (selectedPaidAgt) {
                if (claim.paidAgent !== selectedPaidAgt) return false;
            }

            return true;
        });
    }, [
        allClaims,
        entryFilterActive,
        entryRange,
        reportFilterActive,
        reportRange,
        selectedPaidAgt,
        selectedWritingAgt
    ]);

    const handleClear = () => {
        setSelectedPaidAgt('');
        setSelectedWritingAgt('');
        setEntryFilterActive(false);
        setReportFilterActive(false);
        setShowEntryPicker(false);
        setShowReportPicker(false);
    };

    const exportCsv = (rows, filename = 'commissionDetail.csv') => {
        if (!rows || rows.length === 0) {
            alert('No data to export');
            return;
        }

        const headers = [
            'writingAgent',
            'writingAgentName',
            'share',
            'polNo',
            'ownerDisplayName',
            'premiumAmount',
            'commissionPaid',
            'entryDate',
            'reportDate'
        ];

        const csvContent = [
            headers.join(','), // header row
            ...rows.map(row =>
                headers
                    .map(h => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`)
                    .join(',')
            )
        ].join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="container container-section" id="commission-detail-container">
            {allClaims.length === 0 ? (
                <div className="card notfoundbox rounded-3 shadow-lg">
                    <div className="card-body">
                        <div className="flex-container">
                            <h5>No Commissions Found</h5>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="filter-box">
                        <FilterIcon />

                        {/* Entry Date Range */}
                        <div className="filter-item filter-item-range">
                            <label className="filterLabel">Entry Date Range</label>
                            <input
                                type="text"
                                readOnly
                                className="filterInput"
                                value={formatRangeLabel(entryFilterActive, entryRange)}
                                onClick={() => setShowEntryPicker(prev => !prev)}
                            />
                            {showEntryPicker && (
                                <div ref={entryPickerRef} className="date-range-popover">
                                    <DateRange
                                        editableDateInputs={true}
                                        moveRangeOnFirstSelection={false}
                                        ranges={entryRange}
                                        onChange={(item) => {
                                            setEntryRange([item.selection]);
                                            setEntryFilterActive(true);
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Report Date Range */}
                        <div className="filter-item filter-item-range">
                            <label className="filterLabel">Report Date Range</label>
                            <input
                                type="text"
                                readOnly
                                className="filterInput"
                                value={formatRangeLabel(reportFilterActive, reportRange)}
                                onClick={() => setShowReportPicker(prev => !prev)}
                            />
                            {showReportPicker && (
                                <div ref={reportPickerRef}  className="date-range-popover" >
                                    <DateRange
                                        editableDateInputs={true}
                                        moveRangeOnFirstSelection={false}
                                        ranges={reportRange}
                                        onChange={item => {
                                            setReportRange([item.selection]);
                                            setReportFilterActive(true);
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Writing Agt dropdown */}
                        <div className="filter-item">
                            <label htmlFor="writingAgtSelect" className="filterLabel">
                                Writing Agent
                            </label>
                            <select
                                id="writingAgtSelect"
                                className="filterInput"
                                value={selectedWritingAgt}
                                onChange={e => setSelectedWritingAgt(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value={model.agtCode}>{model.agtCode} - {model.agentInfo.displayName}</option>
                                {downlines.map((dl, i) => (
                                    <option key={i} value={dl.agentCode}>
                                        {dl.agentCode} - {dl.name ?? dl.displayName ?? 'Downline'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Paid Agt dropdown */}
                        <div className="filter-item">
                            <label htmlFor="paidAgtSelect" className="filterLabel">
                                Paid Agent
                            </label>
                            <select
                                id="paidAgtSelect"
                                className="filterInput"
                                value={selectedPaidAgt}
                                onChange={e => setSelectedPaidAgt(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value={model.agtCode}>{model.agtCode} - {model.agentInfo.displayName}</option>
                                {downlines.map((dl, i) => (
                                    <option key={i} value={dl.agentCode}>
                                        {dl.agentCode} - {dl.name ?? dl.displayName ?? 'Downline'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-button-box">
                            <div className="filter-item">
                                <button
                                    type="button"
                                    className="filterButton"
                                    onClick={handleClear}
                                >
                                    Clear
                                </button>
                            </div>
                            <div className="filter-item">
                                <button
                                    type="button"
                                    className="filterButton"
                                    onClick={() =>
                                        exportCsv(filteredClaims, 'commissionDetail_export.csv')
                                    }
                                >
                                    Export CSV
                                </button>
                            </div>
                        </div>
                    </div>
                    <AdminSearchTable columns={columns} data={filteredClaims} />
                </>
            )}
        </div>
    );
};

export default CommissionDetailSection;
